import {
  EDatasourceType,
  ESdChangeReason,
  IActionDefinitionDto,
  IDatasourceDto,
  RightPanelType
} from "@kuika/kuika-cl-model";
import { endsWith } from "lodash";
import ObjectHash from "object-hash";
import { FormInstance } from "rc-field-form";
import * as React from "react";
import { createRef } from "react";
import { connect } from "react-redux";
import { DatasourceService } from "../../../services/datasource-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { IPureDesignComponentProps, PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { SaveStatus } from "../../../shared/utility/save-status";
import { AppState } from "../../../store/root-reducer";
import { setNextCallbackFunction, setShowModal } from "../../../store/unsaved-changes-modal/actions";
import { runParkedNavigation } from "../../../store/unsaved-changes-modal/run-parked-navigation";
import NoElementFoundTreeSvg from "../../../svgs/no-element-found-tree.svg";
import CloseConfirmationModal from "../../common/close-confirmation-modal";
import { DeleteModal } from "../../common/delete-modal";
import { InputTooltip } from "../../common/input-tooltip";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaFormPage, { KuikaFormField, KuikaFormSection } from "../../ui-kit/kuika-form-page/kuika-form-page";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import {
  IKuikaEditGridColumn,
  KuikaEditGrid,
  KuikaEditGridCell,
  KuikaEditGridEmpty,
  KuikaEditGridRow
} from "../shared/kuika-edit-grid/kuika-edit-grid";
import { CreateMethod } from "./bluetooth/create-method";
import { ConnectionRenameTitle } from "./connection-rename-title";
import { requestDatasourceReveal } from "./datasource-reveal";
import { ConnectionSwitcherChip } from "./editor-switcher/connection-switcher-chip";
import "./create-connection-form.scss";
import "./editor-bar-surface.scss";

const HELP_LINK: string = "https://academy.kuika.com/en/content/adding-a-bluetooth-data-source-user-guide";

/** Method, its two counts, and the row menu. The counts sit at the end of their columns. */
const METHOD_COLUMNS: IKuikaEditGridColumn[] = [
  { id: "method", header: "Method", track: "minmax(0, 1fr)", headerTestId: "bluetoothMethodHeaderText" },
  { id: "actions", header: "Actions", track: "120px", align: "end", headerTestId: "bluetoothActionsHeaderText" },
  {
    id: "inputs",
    header: "Method Inputs",
    track: "140px",
    align: "end",
    headerTestId: "bluetoothMethodInputsHeaderText"
  },
  { id: "menu", track: "52px" }
];

interface ICreateConnectionBluetoothProps {
  editingDatasource?: IDatasourceDto;
  showModal: boolean;
  nextCallback?: () => void;
  setShowModal: (showModal: boolean) => void;
  setNextCallbackFunction: (cb?: () => void) => void;
}

interface ICreateConnectionBluetoothState {
  /** The DELETE of a method, which shares the page's busy flag. The save has its own status. */
  isSaveDisabled: boolean;
  /** Where the save stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  name: string;
  isCreateMethodModalOpen: boolean;
  searchText: string;
  isNameOnEdit: boolean;
  methodToDelete?: IActionDefinitionDto;
  isMethodDeleteModalVisible: boolean;
}

class CreateConnectionBluetooth extends PureSdComponent<
  ICreateConnectionBluetoothProps & IWithSDProps,
  ICreateConnectionBluetoothState
> {
  formRef = createRef<FormInstance>();

  objectHash: string = "";

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: ICreateConnectionBluetoothProps & IWithSDProps) {
    super(props);

    this.state = {
      isSaveDisabled: false,
      saveStatus: "idle",
      name: "",
      isCreateMethodModalOpen: false,
      searchText: "",
      isNameOnEdit: false,
      isMethodDeleteModalVisible: false
    };
  }

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  componentDidMount(): void {
    this.props.sd.setActiveRightPanelByClick(RightPanelType.Properties);

    this.handleLoadDatasource();
  }

  componentDidUpdate(
    prevProps: Readonly<IPureDesignComponentProps & IWithSDProps & ICreateConnectionBluetoothProps>
  ): void {
    if (this.props.editingDatasource?.id !== prevProps.editingDatasource?.id) {
      this.handleLoadDatasource();
    }

    // A save the pending navigation asked for ("Update & close") is still running or showing
    // its ✓: its own close runs the navigation. Asking again now would re-open the confirmation.
    if (this.props.nextCallback && this.saveStatus.canStart) {
      this.handleCloseDatasource();
      return;
    }
  }

  handleLoadDatasource = () => {
    const {
      editingDatasource,
      sd: { activeCreatingDatasourceName }
    } = this.props;

    if (editingDatasource) {
      this.setState(
        {
          isSaveDisabled: false,
          name: editingDatasource.name,
          isCreateMethodModalOpen: false,
          searchText: "",
          isNameOnEdit: false
        },
        () => {
          this.formRef.current?.setFieldsValue({
            name: editingDatasource.name,
            bluetoothInfo: editingDatasource.bluetoothInfo
          });
          this.objectHash = this.getObjectHash();
        }
      );
      return;
    }

    this.setState(
      {
        isSaveDisabled: false,
        name: activeCreatingDatasourceName || "",
        isCreateMethodModalOpen: false,
        searchText: "",
        isNameOnEdit: false
      },
      () => {
        this.formRef.current?.setFieldsValue({
          name: activeCreatingDatasourceName,
          bluetoothInfo: {
            id: "",
            name: "",
            address: ""
          }
        });
        this.objectHash = this.getObjectHash();
      }
    );
  };

  handleMethodChange = (id: string) => {
    this.props.sd.setActiveBluetoothMethod(id);
  };

  handleCreateMethod = () => {
    if (this.props.sd.editingBluetoothMethodId) {
      this.props.sd.setEditingBluetoothMethod(undefined);
    }
  };

  editMethod = (id: string) => {
    this.props.sd.setEditingBluetoothMethod(id);
    this.openCreateMethodModal();
  };

  openCreateMethodModal = () => {
    this.setState({ isCreateMethodModalOpen: true });
  };

  closeCreateMethodModal = () => {
    this.setState({ isCreateMethodModalOpen: false });
  };

  openDeleteMethodModal = (method: IActionDefinitionDto) => {
    this.setState({ methodToDelete: method, isMethodDeleteModalVisible: true });
  };

  deleteMethod = async () => {
    const method = this.state.methodToDelete;
    if (!method) return;

    this.setState({ isSaveDisabled: true });
    DatasourceService.DeleteAction({ ...method, bluetoothInfo: JSON.stringify(method.bluetoothInfo) as any })
      .then((_response) => {
        this.props.sd.refreshActions();
        this.props.sd.setActiveBluetoothMethod(undefined);
        this.handleCreateMethod();
        this.props.sd.setEditingBluetoothMethod(undefined);
      })
      .catch((error) => {
        KMainFunctions.exceptionHandler(error);
      })
      .finally(() => {
        this.setState({ isSaveDisabled: false });
      });
  };

  handleNameInputOnEnter = (e: React.KeyboardEvent): void => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    this.handleSubmitNameChange();
  };

  handleSubmitNameChange = () => {
    const errors = this.formRef.current?.getFieldError("name");
    if (errors && errors.length > 0) {
      return;
    }
    this.setState({ isNameOnEdit: false, name: this.formRef.current?.getFieldValue("name") });
  };

  handleAbortNameChange = () => {
    if (!this.state.name) {
      KMainFunctions.displayWarningNotification("Please enter a valid name");
      return;
    }

    this.formRef.current?.setFieldsValue({
      ...this.formRef.current.getFieldsValue(),
      name: this.state.name
    });
    this.setState({ isNameOnEdit: false });
  };

  existingNameValidator = async (_rule, value) => {
    if (!value) return;

    const datasources = this.props.sd.datasources;

    if (
      datasources.some(
        (tmp) => tmp.name.toLowerCase() === value.toLowerCase() && tmp.id !== this.props.editingDatasource?.id
      )
    )
      throw new Error("This Datasource Name is used before.");
  };

  getObjectHash = (): string => {
    const { name } = this.state;
    const values = this.formRef.current?.getFieldsValue();

    return ObjectHash({ name, bluetoothInfo: values?.bluetoothInfo });
  };

  handleSaveDatasource = (formValues) => {
    const { name, isNameOnEdit } = this.state;
    const { editingDatasource } = this.props;

    if (isNameOnEdit) {
      KMainFunctions.displayWarningNotification("You should apply datasource name before save");
      return;
    }
    const isEqualToScreenName = this.props.sd?.screenList?.find(
      (screen) => screen.name.toLowerCase() === name.toLowerCase()
    );
    const isEqualToWfName = this.props.sd?.workFlowList?.find((wf) => wf.name.toLowerCase() === name.toLowerCase());

    if (isEqualToScreenName || isEqualToWfName) {
      this.saveStatus.reset();
      KMainFunctions.displayWarningNotification("Datasource name can not be equal to screen name or Process name.");
      return;
    }
    // The commit button is live again as soon as the ✓ replaces the spinner, so a second press
    // would re-fire the save behind the closing sheet.
    if (!this.saveStatus.canStart) return;
    this.saveStatus.start();

    DatasourceService.SaveDatasource({
      id: editingDatasource?.id,
      name,
      bluetoothInfo: formValues.bluetoothInfo,
      datasourceType: EDatasourceType.BleService
    } as IDatasourceDto)
      .then(async (response): Promise<void> => {
        if (response.status !== 200) {
          // A resolved response that is not 200: the `finally` reset used to rescue this branch.
          this.saveStatus.fail();
          return;
        }
        this.props.sd.refreshActions();
        await this.props.sd.refreshDatabases();
        // The ✓ goes up first and the sheet leaves after it — see `SaveStatus`.
        // A close confirmation this save answered ("Update & close") steps aside at once, so
        // the ✓ on the button is what is seen before the editor leaves.
        this.props.setShowModal(false);
        this.saveStatus.succeed((): void => {
          if (editingDatasource) {
            // Only the edit path goes back to the list; a first save opens the new
            // datasource's own editor, which is its own confirmation.
            requestDatasourceReveal({ name });
            this.closeDataSource();
          } else {
            const ds = response.data.find((datasource) => datasource.name === name);
            this.props.sd.setActiveEditingDatasource(ds);
          }
        });
      })
      .catch((err): void => {
        this.saveStatus.fail();
        KMainFunctions.exceptionHandler(err);
      });
  };

  closeDataSource() {
    this.props.sd.cancelActiveBluetoothMethod();
    this.props.sd.cancelEditingBluetoothMethod();
    if (!runParkedNavigation(this.props.nextCallback, this.props.setNextCallbackFunction)) {
      this.props.sd.cancelCreatingOrEditingDatasource();
    }
  }

  handleCloseDatasource() {
    if (this.objectHash === this.getObjectHash()) {
      this.closeDataSource();
      return;
    }

    this.props.setShowModal(true);
  }

  getMethods = () => {
    const {
      editingDatasource,
      sd: { actionDefinitions }
    } = this.props;
    if (!editingDatasource) return [];

    return actionDefinitions
      .filter(
        (a) => a.kDatasource_Id === editingDatasource.id && a.bluetoothInfo !== null && !endsWith(a.name, "_bluetooth")
      )
      .map((action) => ({
        ...action,
        bluetoothInfo: JSON.parse(action.bluetoothInfo as any)
      }));
  };

  /** A method row's own actions. `danger` is what paints Delete red — not an inline colour. */
  getMethodMenuOptions = (method: IActionDefinitionDto): KuikaDropdownMenuItemType[] => [
    { key: "edit", label: "Edit", onClick: () => this.editMethod(method.id) },
    { key: "delete", label: "Delete", danger: true, onClick: () => this.openDeleteMethodModal(method) }
  ];

  private renderName = (title: string): React.ReactNode => (
    <ConnectionRenameTitle
      title={title}
      identity={
        <ConnectionSwitcherChip
          sd={this.props.sd}
          setNextCallbackFunction={this.props.setNextCallbackFunction}
          current={this.props.editingDatasource}
          name={this.state.name}
          datasourceType={EDatasourceType.BleService}
          triggerTestId="bluetoothConnectionSwitcherTrigger"
          onManageAll={() => this.handleCloseDatasource()}
        />
      }
      isEditing={this.state.isNameOnEdit}
      canDiscard={Boolean(this.state.name)}
      onBeginEdit={() => this.setState({ isNameOnEdit: true })}
      onSubmit={this.handleSubmitNameChange}
      onDiscard={this.handleAbortNameChange}
      placeholder="Name"
      rules={[
        { required: true, message: "Please enter a name" },
        { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Name is invalid" },
        { validator: this.existingNameValidator }
      ]}
      renameTestId="bluetoothConnectionRenameButton"
      inputTestId="bluetoothConnectionNameInput"
    />
  );

  /**
   * The methods of the device, kept as they were: the list is a table of actions the
   * device exposes, not a field, so it stays outside the field grid. A method belongs to
   * a saved datasource, which is why adding one waits for the first save.
   */
  private renderMethods(): React.ReactNode {
    const methods = this.getMethods();
    const matches = methods.filter((method) =>
      method?.name?.toLowerCase().includes(this.state.searchText?.toLowerCase())
    );

    return (
      <div className="kuika-form-section__wide">
        {this.state.isCreateMethodModalOpen && (
          <CreateMethod
            editingDsId={this.props.sd?.activeEditingDatasource?.id || ""}
            editingMethodId={this.props.sd.editingBluetoothMethodId}
            handleCreateMethod={this.handleCreateMethod}
            showModal={this.state.isCreateMethodModalOpen}
            existingMethods={methods}
            closeModal={this.closeCreateMethodModal}
          />
        )}

        <div className="kuika-connection-form__methods-toolbar">
          <KuikaInput
            size="md"
            placeholder="Search"
            value={this.state.searchText}
            onChange={(_e, value) => this.setState({ searchText: String(value ?? "") })}
            leftIcon={<KuikaSvg name="search" width={14} height={14} color="var(--dark-5)" />}
            data-testid="bluetoothMethodSearchInput"
          />
          <KuikaButton
            type="secondary"
            size="md"
            uppercase
            disabled={!this.props.editingDatasource}
            onClick={() => {
              this.openCreateMethodModal();
              this.props.sd.cancelEditingBluetoothMethod();
            }}
            data-testid="bluetoothAddMethodButton"
          >
            Add new method
          </KuikaButton>
        </div>

        {/* The shared grid's frame, read-only: a method is added and edited in its own modal,
            so the list has no draft row — ADD NEW METHOD above is how one is made. */}
        <KuikaEditGrid columns={METHOD_COLUMNS} label="Methods" testId="bluetoothMethodsGrid">
          {methods.length === 0 && <KuikaEditGridEmpty tall>No Bluetooth Method exists.</KuikaEditGridEmpty>}
          {methods.length > 0 && matches.length === 0 && (
            <KuikaEditGridEmpty tall>
              <NoElementFoundTreeSvg />
            </KuikaEditGridEmpty>
          )}
          {[...matches]
            .sort((a, b) => (a.name > b.name ? 1 : -1))
            .map((method) => {
              if (!method.bluetoothInfo) return null;
              return (
                <KuikaEditGridRow
                  key={method.id}
                  selected={this.props.sd.activeBluetoothMethodId === method.id}
                  onClick={() => this.handleMethodChange(method.id)}
                  testId={`${method.name}BluetoothMethodRow`}
                >
                  <KuikaEditGridCell>{method.name}</KuikaEditGridCell>
                  <KuikaEditGridCell end>{method.bluetoothInfo.actions?.length ?? 0}</KuikaEditGridCell>
                  <KuikaEditGridCell end>{method.bluetoothInfo.inputs?.length ?? 0}</KuikaEditGridCell>
                  <KuikaEditGridCell center>
                    <div className="kuika-edit-grid__row-menu">
                      <KuikaDropdownMenu options={this.getMethodMenuOptions(method)} placement="bottomRight">
                        <KuikaButton type="text" isIcon aria-label={`${method.name} actions`}>
                          <KuikaSvg name="dots-vertical" type="styling-panel-icons" width={14} height={14} />
                        </KuikaButton>
                      </KuikaDropdownMenu>
                    </div>
                  </KuikaEditGridCell>
                </KuikaEditGridRow>
              );
            })}
        </KuikaEditGrid>
      </div>
    );
  }

  render() {
    return (
      <>
        <KuikaFormPage
          className="kuika-connection-form"
          title={this.state.name}
          formName="bluetoothDatasource"
          formRef={this.formRef}
          onFinish={this.handleSaveDatasource}
          status={this.state.saveStatus}
          isLoading={this.state.isSaveDisabled}
          renderTitle={this.renderName}
          submitLabel={this.props.editingDatasource ? "UPDATE" : "CREATE"}
          submitTestId="bluetoothConnectionSubmitButton"
          cancelTestId="bluetoothConnectionCancelButton"
          onCancel={() => this.handleCloseDatasource()}
        >
          <KuikaFormSection legend="Device" hint="How Kuika finds the device" columns={2}>
            <KuikaFormField
              name={["bluetoothInfo", "name"]}
              label="Device name"
              labelSuffix={
                <InputTooltip
                  description="You must enter the name of the device you want to connect via Bluetooth"
                  helpLink={HELP_LINK}
                />
              }
              rules={[
                { required: true, message: "Device Name is required" },
                { pattern: /^[A-Za-z][A-Za-z0-9 ]*$/, message: "Please enter a valid Device Name." }
              ]}
            >
              <KuikaInput size="md" placeholder="Enter Device Name" data-testid="bluetoothDeviceNameInput" />
            </KuikaFormField>

            <KuikaFormField
              name={["bluetoothInfo", "id"]}
              label="Device ID"
              labelSuffix={
                <InputTooltip
                  description="If you have the ID information of the device you want to connect via Bluetooth, you can enter the device ID."
                  helpLink={HELP_LINK}
                />
              }
            >
              <KuikaInput size="md" placeholder="Enter Device ID" data-testid="bluetoothDeviceIdInput" />
            </KuikaFormField>

            <KuikaFormField
              name={["bluetoothInfo", "address"]}
              label="Device address"
              labelSuffix={
                <InputTooltip
                  description="If you have the address information of the device you want to connect via Bluetooth, you can enter the device address information."
                  helpLink={HELP_LINK}
                />
              }
              extra="Six hex pairs, e.g. 00:1A:7D:DA:71:13"
              rules={[{ pattern: /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/, message: "Device Address is not valid" }]}
            >
              <KuikaInput size="md" placeholder="Enter Device Address" data-testid="bluetoothDeviceAddressInput" />
            </KuikaFormField>
          </KuikaFormSection>

          <KuikaFormSection legend="Methods" hint="What the device exposes to your screens">
            {this.renderMethods()}
          </KuikaFormSection>
        </KuikaFormPage>

        <DeleteModal
          itemName={this.state.methodToDelete?.name ?? ""}
          triggerModal={this.state.isMethodDeleteModalVisible}
          deleteItem={() => this.deleteMethod()}
          deleteType={"method"}
          closeModal={() => this.setState({ isMethodDeleteModalVisible: false })}
        />

        <CloseConfirmationModal
          itemName={this.state.name}
          closeType={"Bluetooth Connection"}
          triggerModal={this.props.showModal}
          isLoading={this.state.saveStatus === "loading"}
          updateAndCloseHandler={() => this.formRef.current?.submit()}
          closeWithoutSaveHandler={() => {
            this.props.setShowModal(false);
            this.closeDataSource();
          }}
          cancel={() => {
            this.props.setNextCallbackFunction();
            this.props.setShowModal(false);
          }}
        />
      </>
    );
  }

  checkNeedRender = (_changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const mapStateToProps = (state: AppState) => ({
  nextCallback: state.unsavedChangesModal.nextCallback,
  showModal: state.unsavedChangesModal.showModal
});

const mapDispatchToProps = (dispatch) => ({
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb)),
  setShowModal: (showModal: boolean) => dispatch(setShowModal(showModal, null))
});

const createConnectionBluetooth = connect(
  mapStateToProps,
  mapDispatchToProps
)(withSD<ICreateConnectionBluetoothProps>(CreateConnectionBluetooth));
export { createConnectionBluetooth as CreateConnectionBluetooth };
